import { Box, Stack, Typography, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useWeeklyWeatherInfo } from "./api/useWeeklyWeatherInfo";
import { useHourlyWeatherInfo } from "./api/useHourlyWeatherInfo";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { getWeatherIcon } from "./weatherUtils";

const WeatherMain = () => {
  const navigate = useNavigate();
  const headerRef = useRef<HTMLDivElement>(null);

  const [activeDateLabel, setActiveDateLabel] = useState("오늘");
  const [activeDateValue, setActiveDateValue] = useState(dayjs().format("MM-DD"));

  const { data: weeklyWeatherData, isSuccess: isWeeklySuccess } = useWeeklyWeatherInfo();
  const { data: hourlyWeatherInfo, isSuccess: isHourlySuccess } = useHourlyWeatherInfo()
  const { weeklyWeatherInfo, regionText } = useMemo(() => {
    return {
      weeklyWeatherInfo: weeklyWeatherData?.weeklyWeatherInfo || [],
      regionText: weeklyWeatherData?.region || ""
    };
  }, [weeklyWeatherData]);

  const { yesterday, today, tomorrow, dayAfter } = useMemo(() => {
    if (!hourlyWeatherInfo) return { yesterday: [], today: [], tomorrow: [], dayAfter: [] };

    const now = dayjs();
    const yesterdayStr = now.subtract(1, "day").format("YYYYMMDD");
    const todayStr = now.format("YYYYMMDD");
    const tomorrowStr = now.add(1, "day").format("YYYYMMDD");
    const dayAfterStr = now.add(2, "day").format("YYYYMMDD");

    return {
      yesterday: hourlyWeatherInfo.filter((item) => item.date === yesterdayStr),
      today: hourlyWeatherInfo.filter((item) => item.date === todayStr),
      tomorrow: hourlyWeatherInfo.filter((item) => item.date === tomorrowStr),
      dayAfter: hourlyWeatherInfo.filter((item) => item.date === dayAfterStr),
    };
  }, [hourlyWeatherInfo]);

  const displayHourlyWeather = useMemo(() => {
    const currentHourStr = dayjs().format("HH00");
    const todayFuture = today.filter((item) => item.date === dayjs().format("YYYYMMDD") && item.time >= currentHourStr);
    return [...todayFuture, ...tomorrow, ...dayAfter];
  }, [today, tomorrow, dayAfter]);

  const currentHourWeather = useMemo(() => {
    const currentHourStr = dayjs().format("HH00");
    return today.find((item) => item.time === currentHourStr);
  }, [today]);

  const { minTemp, maxTemp } = useMemo(() => {
    if (today.length === 0) return { minTemp: null, maxTemp: null };
    const temps = today.map((item) => item.temp);
    return {
      minTemp: Math.min(...temps),
      maxTemp: Math.max(...temps),
    };
  }, [today]);

  const diffText = useMemo(() => {
    if (!currentHourWeather || yesterday.length === 0) return "";

    const todayTemp = currentHourWeather.temp;
    const yesterdayItem = yesterday.find((item) => item.time === currentHourWeather.time);

    if (!yesterdayItem) return "";

    const diff = todayTemp - yesterdayItem.temp;
    const absDiff = Math.round(Math.abs(diff) * 10) / 10;

    if (diff > 0) return `어제보다 ${absDiff}° 높습니다.`;
    if (diff < 0) return `어제보다 ${absDiff}° 낮습니다.`;
    return `어제와 기온이 같습니다.`;
  }, [currentHourWeather, yesterday]);

  const combinedWeeklyForecast = useMemo(() => {
    if (!hourlyWeatherInfo || !weeklyWeatherInfo) return [];

    const result = [];

    const dayNames = ["일", "월", "화", "수", "목", "금", "토"];

    // 내일(Tomorrow)
    if (tomorrow.length > 0) {
      const date = dayjs().add(1, "day");
      const am = tomorrow.find(i => i.time === "0900") || tomorrow[0];
      const pm = tomorrow.find(i => i.time === "1500") || tomorrow[Math.floor(tomorrow.length / 2)];
      result.push({
        date: date.format("YYYYMMDD"),
        displayDate: date.format("MM-DD"),
        dayName: "내일",
        minTemp: Math.min(...tomorrow.map(i => i.temp)),
        maxTemp: Math.max(...tomorrow.map(i => i.temp)),
        amIcon: getWeatherIcon(am.rainType, am.skyStatus, am.time),
        pmIcon: getWeatherIcon(pm.rainType, pm.skyStatus, pm.time),
        isSunday: date.day() === 0
      });
    }

    // 모레(Day After)
    if (dayAfter.length > 0) {
      const date = dayjs().add(2, "day");
      const am = dayAfter.find(i => i.time === "0900") || dayAfter[0];
      const pm = dayAfter.find(i => i.time === "1500") || dayAfter[Math.floor(dayAfter.length / 2)];
      result.push({
        date: date.format("YYYYMMDD"),
        displayDate: date.format("MM-DD"),
        dayName: dayNames[date.day()],
        minTemp: Math.min(...dayAfter.map(i => i.temp)),
        maxTemp: Math.max(...dayAfter.map(i => i.temp)),
        amIcon: getWeatherIcon(am.rainType, am.skyStatus, am.time),
        pmIcon: getWeatherIcon(pm.rainType, pm.skyStatus, pm.time),
        isSunday: date.day() === 0
      });
    }

    // 나머지 weeklyWeatherInfo (모레 이후 데이터)
    const dayAfterNextStr = dayjs().add(3, "day").format("YYYYMMDD");
    const restOfWeekly = weeklyWeatherInfo
      .filter(item => item.date >= dayAfterNextStr)
      .slice(0, 6);

    restOfWeekly.forEach(item => {
      const date = dayjs(item.date);
      result.push({
        date: item.date,
        displayDate: date.format("MM-DD"),
        dayName: dayNames[date.day()],
        minTemp: item.minTemp,
        maxTemp: item.maxTemp,
        amIcon: getWeatherIcon(item.rainTypeAm, item.skyAm, "0900"),
        pmIcon: getWeatherIcon(item.rainTypePm, item.skyPm, "1500"),
        isSunday: date.day() === 0
      });
    });

    return result;
  }, [hourlyWeatherInfo, weeklyWeatherInfo, tomorrow, dayAfter]);

  if (!isWeeklySuccess || !isHourlySuccess) return null;

  return (
    <Box sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white'
    }}>
      {/* Header */}
      <Box
        ref={headerRef}
        sx={{
          flexShrink: 0
        }}
      >
        <Stack
          component="header"
          direction="row"
          spacing={1}
          useFlexGap
          sx={{
            alignItems: "center",
            px: 2,
            py: 1,
            minHeight: 52,
            backgroundColor:
              currentHourWeather?.rainType === "없음" && currentHourWeather?.skyStatus === "맑음"
                ? "#FFEFCE"
                : "#DEE1EB",
          }}
        >
          <IconButton aria-label="뒤로가기" onClick={() => navigate("/")}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
            날씨
          </Typography>
        </Stack>
      </Box>

      {/* Content */}
      <Box component='main'
        sx={{
          flex: 1,
          overflowX: "hidden",
          overflowY: "auto",
          position: 'relative',
          background: (currentHourWeather?.rainType === '없음' && currentHourWeather?.skyStatus === '맑음')
            ? 'linear-gradient(180deg, #FFEFCE 0%, #FFFFFF 220px, #FFFFFF 30%)'
            : 'linear-gradient(180deg, #DEE1EB 0%, #FFFFFF 220px, #FFFFFF 30%)',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>

        <Box sx={{
          width: '100%',
          maxWidth: 560,
          position: 'relative',
          zIndex: 1,
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: 3,
          boxSizing: 'border-box',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          {/* 날씨별 상단 그라데이션 배경 (컨텐츠와 함께 스크롤) */}
          {/* <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '440px',
            background: (currentHourWeather?.rainType === '없음' && currentHourWeather?.skyStatus === '맑음')
              ? 'linear-gradient(180deg, #FFEFCE 0%, #FFFFFF 30%)'
              : 'linear-gradient(180deg, #DEE1EB 0%, #FFFFFF 30%)',
            zIndex: -1
          }} /> */}
          <Stack spacing={6} useFlexGap sx={{ width: '100%' }}>
            <Stack spacing={2} useFlexGap sx={{ alignItems: 'center' }}>
              <Box
                component='img'
                src={getWeatherIcon(currentHourWeather?.rainType, currentHourWeather?.skyStatus, currentHourWeather?.time)}
                sx={{
                  width: 100,
                  height: 100,
                  objectFit: 'cover'
                }}
              />
              <Stack spacing={1} useFlexGap sx={{ color: '#0A0A0A', alignItems: 'center' }}>
                <Typography variant="subtitle2" component='p' sx={{ fontSize: '32px', lineHeight: 1, }}>
                  {currentHourWeather?.temp}°
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1 }}>
                  {diffText}
                </Typography>
              </Stack>
            </Stack>

            <Box sx={{
              width: '100%',
              display: 'flex'
            }}>
              <Stack spacing={1.5} useFlexGap sx={{ flex: 1, alignItems: 'center' }}>
                <Typography variant="h6" component='p' sx={{ fontWeight: 600, lineHeight: 1 }}>
                  최저
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 24, color: '#0896DD' }}>
                  {minTemp !== null ? minTemp.toFixed(1) + "°" : "-"}
                </Typography>
              </Stack>
              <Stack spacing={1.5} useFlexGap sx={{ flex: 1, alignItems: 'center' }}>
                <Typography variant="h6" component='p' sx={{ fontWeight: 600, lineHeight: 1 }}>
                  최고
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 24, color: '#FF4D4D' }}>
                  {maxTemp !== null ? maxTemp.toFixed(1) + "°" : "-"}
                </Typography>
              </Stack>
            </Box>

            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              borderRadius: 4,
              backgroundColor: '#F6F6F6',
              px: 3,
              py: 2,
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Stack direction='row' spacing={1} useFlexGap sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ lineHeight: 1, fontWeight: 500 }}>
                    {activeDateLabel}
                  </Typography>
                  <Typography variant="h6" component='p' sx={{ lineHeight: 1, fontWeight: 400, color: '#96949E' }}>
                    {activeDateValue}
                  </Typography>
                </Stack>
              </Box>

              <Box sx={{ width: '100%', position: 'relative' }}>
                <Swiper
                  modules={[Navigation]}
                  slidesPerView={5}
                  slidesPerGroup={5}
                  spaceBetween={20}
                  slidesOffsetBefore={12}
                  slidesOffsetAfter={12}
                  navigation={{
                    nextEl: '.hourly-next',
                    prevEl: '.hourly-prev',
                  }}
                  onSlideChange={(swiper: { activeIndex: number }) => {
                    const currentItem = displayHourlyWeather[swiper.activeIndex];
                    if (!currentItem) return;

                    const date = currentItem.date;
                    const todayStr = dayjs().format("YYYYMMDD");
                    const tomorrowStr = dayjs().add(1, "day").format("YYYYMMDD");
                    const dayAfterStr = dayjs().add(2, "day").format("YYYYMMDD");

                    if (date === todayStr) setActiveDateLabel("오늘");
                    else if (date === tomorrowStr) setActiveDateLabel("내일");
                    else if (date === dayAfterStr) setActiveDateLabel("모레");
                    else setActiveDateLabel("예보");

                    setActiveDateValue(dayjs(date).format("MM-DD"));
                  }}
                  style={{ width: '100%' }}
                >
                  {displayHourlyWeather.map((item, index) => {
                    const isTomorrow = item.date === dayjs().add(1, "day").format("YYYYMMDD");
                    const isDayAfter = item.date === dayjs().add(2, "day").format("YYYYMMDD");

                    let timeLabel = `${parseInt(item.time.substring(0, 2))}시`;
                    let timeColor = '#96949E';

                    if (isTomorrow) {
                      timeColor = '#9063FF';
                      if (item.time === '0000') timeLabel = '내일';
                    } else if (isDayAfter) {
                      timeColor = '#26D698';
                      if (item.time === '0000') timeLabel = '모레';
                    }

                    return (
                      <SwiperSlide key={`hourly-${index}`}>
                        <Stack spacing={1} useFlexGap sx={{ alignItems: 'center' }}>
                          <Typography variant="h6" component='p' sx={{ lineHeight: 1, }}>
                            {item.temp}°
                          </Typography>
                          <Box
                            component="img"
                            src={getWeatherIcon(item.rainType, item.skyStatus, item.time)}
                            sx={{ width: 20, height: 20 }}
                          />
                          <Typography
                            variant="h6"
                            component='p'
                            sx={{
                              color: timeColor,
                              lineHeight: 1,
                              fontWeight: (item.time === '0000' && (isTomorrow || isDayAfter)) ? 700 : 500,
                              border: '1px solid transparent',
                              borderRadius: '10px',
                              px: '6px',
                              py: '1px',
                              whiteSpace: 'nowrap',
                              ...((item.time === '0000' && (isTomorrow || isDayAfter)) && {
                                borderColor: timeColor,
                              })
                            }}
                          >
                            {timeLabel}
                          </Typography>
                        </Stack>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
                <IconButton
                  className="hourly-prev"
                  sx={{
                    position: 'absolute',
                    left: -12,
                    top: '50%',
                    transform: 'translateY(-58%)',
                    zIndex: 10,
                    backgroundColor: 'white',
                    boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
                    width: 24,
                    height: 24,
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    },
                    '&.swiper-button-disabled': {
                      display: 'none'
                    }
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  className="hourly-next"
                  sx={{
                    position: 'absolute',
                    right: -12,
                    top: '50%',
                    transform: 'translateY(-58%)',
                    zIndex: 10,
                    backgroundColor: 'white',
                    boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
                    width: 24,
                    height: 24,
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    },
                    '&.swiper-button-disabled': {
                      display: 'none'
                    }
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 20 }} />
                </IconButton>

              </Box>
            </Box>

            <Stack spacing={1.5} useFlexGap>
              <Stack direction='row' spacing={1.25} useFlexGap sx={{ alignItems: 'center', px: 0.25 }}>
                <Typography variant="subtitle2" component='p' sx={{ lineHeight: 1 }}>주중 날씨 예보</Typography>
                <Typography variant="body2" component='p' sx={{ lineHeight: 1, color: '#96949E' }}>{regionText}</Typography>
              </Stack>
              {/* 주간 날씨 리스트 구현 */}
              <Stack spacing={1} useFlexGap>
                {combinedWeeklyForecast.map((day, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#F6F6F6 !important',
                      borderRadius: 4,
                      px: 3,
                      py: 2,
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    {/* 날짜 정보 */}
                    <Stack spacing={0.5} useFlexGap>
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1,
                          fontWeight: 500,
                          color: day.isSunday ? '#FF4D4D' : '#0A0A0A'
                        }}
                      >
                        {day.dayName}
                      </Typography>
                      <Typography variant="h6" component='p' sx={{ lineHeight: 1, fontWeight: 400, color: '#96949E' }}>
                        {day.displayDate}
                      </Typography>
                    </Stack>

                    {/* 날씨 아이콘 (오전/오후) */}
                    <Stack direction='row' spacing={2} useFlexGap sx={{ alignItems: 'center' }}>
                      <Box component="img" src={day.amIcon} sx={{ width: 24, height: 24 }} />
                      <Box component="img" src={day.pmIcon} sx={{ width: 24, height: 24 }} />
                    </Stack>

                    {/* 기온 정보 */}
                    <Stack direction='row' spacing={1.5} useFlexGap sx={{ alignItems: 'center', minWidth: 80, justifyContent: 'flex-end' }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#0896DD' }}>
                        {Math.round(day.minTemp)}°
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#D4D4D4' }}>|</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#FF4D4D' }}>
                        {Math.round(day.maxTemp)}°
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Stack>

            </Stack>

          </Stack>
        </Box>
      </Box>
    </Box>
  )
}

export default WeatherMain
